import { Box, Flex, Input, Show } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftText from '@/components/Atoms/FaceliftText';
import { useSendOTP } from '@/hooks/service-hooks/auth';

const emailSchema = yup.object().shape({
	email: yup
		.string()
		.email('Please enter a valid email address')
		.required('Email is required'),
});

type EmailFormData = yup.InferType<typeof emailSchema>;

const TwoFactorEmailForm = ({
	onSuccess,
	prefilledEmail,
}: {
	onSuccess: (email: string) => void;
	prefilledEmail?: string;
}) => {
	const [isSubmitting, setIsSubmitting] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors },
		getValues,
	} = useForm<EmailFormData>({
		resolver: yupResolver(emailSchema),
		mode: 'onSubmit',
		defaultValues: {
			email: prefilledEmail || '',
		},
	});

	const { mutate: sendOTP } = useSendOTP(
		() => {
			setIsSubmitting(false);
			toast.success('OTP sent successfully to your email');
			const emailValue = getValues('email');
			onSuccess(emailValue);
		},
		(error) => {
			setIsSubmitting(false);
			const apiError = error as {
				response?: { data?: { message?: string } };
			};
			toast.error(
				apiError?.response?.data?.message ||
					'Failed to send OTP. Please try again.',
			);
		},
	);

	const onSubmit = (data: EmailFormData) => {
		setIsSubmitting(true);
		sendOTP({ email: data.email });
	};

	return (
		<Box>
			<form onSubmit={handleSubmit(onSubmit)}>
				<Flex flexDirection="column" gap={4}>
					<Flex flexDirection="column" gap={2}>
						<FaceliftText
							fontSize="xl"
							fontWeight="bold"
							color="brand.textDark"
							textAlign="start"
						>
							Multi Factor Authentication
						</FaceliftText>
						<FaceliftText
							fontSize="sm"
							color="brand.textDark"
							textAlign="start"
						>
							Enter your email address below. We'll send you a one-time verification code to confirm your identity.
						</FaceliftText>
					</Flex>

					<Box>
						<Input
							disabled={isSubmitting || !!prefilledEmail}
							{...register('email')}
							bg="brand.inputBg"
							variant="outline"
							size="md"
							placeholder="Email address"
							_placeholder={{
								color: { _dark: 'brand.lightGray' },
							}}
							borderColor={errors.email ? 'brand.errors' : undefined}
							type="email"
						/>
						<Show when={errors.email}>
							<FaceliftText color="brand.errors" fontSize="xs" mt={1} ml={1}>
								{errors.email?.message}
							</FaceliftText>
						</Show>
					</Box>

					<FaceliftButton
						mt={4}
						type="submit"
						borderRadius="6px"
						size="md"
						transition="all 0.3s ease"
						_hover={{
							boxShadow: `0 0 10px 3px`,
							border: 'none',
							transform: 'scale(1.02)',
						}}
						letterSpacing={'widest'}
						loading={isSubmitting}
						disabled={isSubmitting}
					>
						Send OTP
					</FaceliftButton>
				</Flex>
			</form>
		</Box>
	);
};

export default TwoFactorEmailForm;
